import { Box, ButtonBase } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import React from 'react';

import { SEGMENT_HEIGHT, segmentHighlightSx, segmentSx, segmentTrackSx } from '../Segmented';

export interface SegmentedOption {
  label: React.ReactNode;
  value: string;
  disabled?: boolean;
  lang?: string;
  ariaLabel?: string;
}

export interface SegmentedControlProps {
  value: string;
  options: SegmentedOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  'aria-label'?: string;
  sx?: SxProps<Theme>;
}

const asArray = (sx: SxProps<Theme> | undefined) => (Array.isArray(sx) ? sx : [sx]);

/** Sized to its options unless asked to fill; dimmed, still showing the choice, while disabled. */
const groupSx =
  (fullWidth: boolean, disabled: boolean) =>
  (theme: Theme): SystemStyleObject<Theme> => ({
    display: fullWidth ? 'grid' : 'inline-grid',
    width: fullWidth ? '100%' : 'auto',
    maxWidth: '100%',
    boxSizing: 'border-box',
    opacity: disabled ? theme.palette.action.disabledOpacity : 1,
  });

/**
 * A choice between a few options on the page, in the same look as SegmentedNav, which switches between pages: the
 * chosen option sits on a Primary highlight that slides to the next choice. Each option is a button that says
 * whether it is pressed.
 */
export const SegmentedControl = React.forwardRef<HTMLDivElement, SegmentedControlProps>(
  ({ value, options, onChange, disabled = false, size = 'small', fullWidth = false, 'aria-label': ariaLabel, sx }, ref) => {
    const theme = useTheme();
    const height = size === 'medium' ? theme.controlHeight.small : SEGMENT_HEIGHT;
    const chosenIndex = options.findIndex((option) => option.value === value);
    return (
      <Box ref={ref} role="group" aria-label={ariaLabel} sx={[segmentTrackSx, groupSx(fullWidth, disabled), ...asArray(sx)]}>
        {chosenIndex >= 0 && <Box aria-hidden="true" sx={segmentHighlightSx(options.length, chosenIndex)} />}
        {options.map((option, index) => {
          const chosen = index === chosenIndex;
          return (
            <ButtonBase
              key={option.value}
              disableRipple
              aria-pressed={chosen}
              aria-label={option.ariaLabel}
              lang={option.lang}
              disabled={disabled || option.disabled}
              onClick={chosen ? undefined : () => onChange(option.value)}
              sx={segmentSx(chosen, height)}
            >
              {option.label}
            </ButtonBase>
          );
        })}
      </Box>
    );
  },
);

SegmentedControl.displayName = 'SegmentedControl';
